import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaLock, FaInfoCircle, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './ManagerChangePassword.css';
import { useNavigate } from 'react-router-dom';

const ManagerChangePassword = () => {
    const baseUrl = "https://api.burrayhr.com";
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [errors, setErrors] = useState({});
    const [showPasswords, setShowPasswords] = useState({
        old_password: false,
        new_password: false,
        confirm_password: false
    });

    // Fetch profile data when component mounts
    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/profile/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log(response.data);
            setUserProfile(response.data);
            
            // Only set the old_password to employee_id if it's first login
            if (response.data.first_login) {
                setPasswordData(prev => ({
                    ...prev,
                    old_password: response.data.employee_data.employee_id
                }));
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                localStorage.clear();
                navigate('/login');
            }
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch profile data',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validatePasswordChange = () => {
        const newErrors = {};
        if (!passwordData.old_password) newErrors.old_password = 'Current password is required';
        if (!passwordData.new_password) newErrors.new_password = 'New password is required';
        else if (passwordData.new_password.length < 8) newErrors.new_password = 'Password must be at least 8 characters';
        if (!passwordData.confirm_password) newErrors.confirm_password = 'Please confirm your password';
        else if (passwordData.new_password !== passwordData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationErrors = validatePasswordChange();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${baseUrl}/api/change-password/`, passwordData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Password changed successfully',
                confirmButtonColor: '#1e3a8a'
            });
            navigate('/manager/dashboard');

            setPasswordData({
                old_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.old_password || 'Failed to change password',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!userProfile) {
        return (
                <>
                <Topbar/>
                <Sidebar/>
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading profile...</p>
                </div>
                </>

        );
    }

    return (
        <div>
            <Topbar/>
            <Sidebar/>
                <div className="main-content" >
                    <div className="change-password-container" >
                        <div className="settings-section">
                            <div className="section-header" style={{
                                textAlign: 'center',
                                marginBottom: '2rem'
                            }}>
                                <h2 style={{
                                    color: '#1e3a8a',
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    marginBottom: '0.5rem'
                                }}>Change Password</h2>
                                <p style={{
                                    color: '#6b7280',
                                    fontSize: '0.875rem'
                                }}>Update password for {userProfile?.employee_data?.full_name}</p>
                            </div>
                            <form onSubmit={handleSubmit} className="settings-form">
                                <div className="form-group">
                                    <label htmlFor="current_password">
                                        <FaLock className="field-icon" />
                                        Current Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={showPasswords.old_password ? 'text' : 'password'}
                                            id="old_password"
                                            name="old_password"
                                            value={passwordData.old_password}
                                            onChange={handlePasswordChange}
                                            className={errors.old_password ? 'error' : ''}
                                            readOnly={userProfile.first_login}
                                            placeholder={userProfile.first_login ? "Employee ID (Auto-filled)" : "Enter current password"}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, old_password: !prev.old_password }))}
                                        >
                                            {showPasswords.old_password ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    {errors.old_password && (
                                        <div className="error-message">
                                            <FaInfoCircle /> {errors.old_password}
                                        </div>
                                    )}
                                    {userProfile.first_login && (
                                        <div className="info-message" style={{ 
                                            color: '#4b5563', 
                                            fontSize: '0.75rem', 
                                            marginTop: '0.25rem' 
                                        }}>
                                            <FaInfoCircle /> Using your Employee ID for first-time login
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="new_password">
                                        <FaLock className="field-icon" />
                                        New Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={showPasswords.new_password ? 'text' : 'password'}
                                            id="new_password"
                                            name="new_password"
                                            value={passwordData.new_password}
                                            onChange={handlePasswordChange}
                                            className={errors.new_password ? 'error' : ''}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, new_password: !prev.new_password }))}
                                        >
                                            {showPasswords.new_password ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    {errors.new_password && (
                                        <div className="error-message">
                                            <FaInfoCircle /> {errors.new_password}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirm_password">
                                        <FaLock className="field-icon" />
                                        Confirm New Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={showPasswords.confirm_password ? 'text' : 'password'}
                                            id="confirm_password"
                                            name="confirm_password"
                                            value={passwordData.confirm_password}
                                            onChange={handlePasswordChange}
                                            className={errors.confirm_password ? 'error' : ''}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, confirm_password: !prev.confirm_password }))}
                                        >
                                            {showPasswords.confirm_password ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    {errors.confirm_password && (
                                        <div className="error-message">
                                            <FaInfoCircle /> {errors.confirm_password}
                                        </div>
                                    )}
                                </div>
                                <button 
                                    type="submit" 
                                    className="submit-button" 
                                    disabled={loading}
                                    style={{
                                        backgroundColor: '#1e3a8a',
                                        color: 'white',
                                        padding: '0.75rem',
                                        borderRadius: '4px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.5rem',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.7 : 1,
                                        transition: 'all 0.2s',
                                        border: 'none',
                                        marginTop: '1rem',
                                        width: '100%'
                                    }}
                                >
                                    {loading ? 'Changing Password...' : 'Change Password'} <FaCheck />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            
        </div>
    );
};

export default ManagerChangePassword;